import { Routes } from 'constants/pages';
import { clearUserData, isLogged, signIn } from 'ducks/user';
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';

/**
 * Encapsulates all the logic necessary to sign in then perform proper navigation
 * @returns a sign in function that takes the form values
 */
export function useSignIn<T>() {
  const dispatch = useDispatch();
  const [params] = useSearchParams();
  const navigate = useNavigate();

  /**
   * Upon successful login, this function is called to perform navigation
   * to the appropriate destination
   */
  const proceed = React.useCallback(
    (confirmation?: { email: string }) => {
      if (confirmation) {
        const to = `${Routes.EmailConfirmation}?email=${encodeURIComponent(confirmation.email)}`;
        navigate(to);
      }

      const redirect = params.get('redirect');
      const to = redirect || Routes.Home;
      navigate(to, { replace: true });
    },
    [navigate, params]
  );

  /**
   * The handler for submitting the form data
   */
  const onSubmit = React.useCallback(
    async (values: T) => {
      dispatch(clearUserData());
      localStorage.clear();

      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      const data: { email_validated: boolean; email: string } = await dispatch(signIn(values));
      // eslint-disable-next-line @typescript-eslint/naming-convention
      const { email_validated, email } = data || {};
      if (!email_validated) {
        proceed({ email });
      } else {
        proceed();
      }
    },
    [dispatch, proceed]
  );

  // Continue to redirect if already logged in upon mount
  const loggedIn = useSelector(isLogged);
  React.useEffect(() => {
    if (loggedIn) {
      proceed();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return onSubmit;
}
